import { useEffect, useState } from 'react';
import { Checkbox } from '@mui/material';
import { CollapsibleSectionProps, OptionProps } from './collapsible-section.props';
import './collapsible-section.css';
import FilterDropdown from '../filter-dropdown';

function CollapsibleSection({
  children,
  title,
  isToggleEnable,
  dropdownOptions,
  onChange,
  placeholder,
  piiTabVisible,
  setSelectedOption,
  selectedOption,
  setInitialTabVisible,
  piiIdentifiers,
  errorMessage,
  isSelectionDisabled,
}: CollapsibleSectionProps) {
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    if (piiTabVisible) {
      setIsChecked(true);
    } else {
      setIsChecked(false);
    }
  }, [piiTabVisible]);

  useEffect(() => {
    if (dropdownOptions && selectedOption) {
      Object.keys(piiIdentifiers ?? {}).forEach((region) => {
        const regionOption = dropdownOptions.find(
          (option) => option.label.toLowerCase() === region,
        );
        if (piiIdentifiers?.[region].length && regionOption) {
          setSelectedOption?.((prev) => {
            if (!prev?.some((item) => (item as { id: number }).id === regionOption.id)) {
              return [...(prev ?? []), regionOption] as OptionProps[];
            }
            return prev as OptionProps[];
          });
        }
      });
    }
  }, [dropdownOptions, piiIdentifiers]);

  const handleOnChange = () => {
    setInitialTabVisible?.(!isChecked);
    const globalOption = dropdownOptions?.find((option) => option.id === 1);
    setIsChecked(!isChecked);
    if (!isChecked) {
      setSelectedOption?.([globalOption as OptionProps]);
    }
    if (globalOption && !selectedOption!.some((item: { id: number }) => item.id === 1)) {
      setSelectedOption?.(() => [globalOption]);
    }
  };

  return (
    <div className="collapsible-section w-full rounded-lg border-solid border-[#D4D4D8] border-[1px] p-3 bg-white">
      <div className={`flex justify-between w-full items-center ${isChecked ? 'pb-[11px]' : ''}`}>
        <div className="flex items-center">
          <Checkbox
            disableRipple
            sx={{
              padding: '2px',
              '& .MuiSvgIcon-root': { fontSize: 18 },
              '&.Mui-checked': {
                border: '1px solid #0A8BC7',
                color: '#0A8BC7',
              },
            }}
            className="hover:cursor-pointer"
            checked={isChecked}
            onChange={handleOnChange}
            disabled={isSelectionDisabled}
          />
          <h2
            className={`font-bold text-lg ml-2 ${
              isSelectionDisabled ? 'text-[#f5b180]' : 'text-[#0A8BC7]'
            }`}
          >
            {title}
          </h2>
        </div>
        {isChecked && isToggleEnable && (
          <div className="shrink-0 w-[20%]">
            <FilterDropdown
              multiple
              value={selectedOption?.map((o) => String(o.id)) ?? []}
              onChange={(selectedIds: string[]) => {
                const newSelected = (selectedIds as string[])
                  .map((idStr) => {
                    const id = Number(idStr);
                    return dropdownOptions?.find((o) => o.id === id);
                  })
                  .filter((o): o is OptionProps => o !== undefined);
                setSelectedOption?.(newSelected);
                const selectedObj = newSelected.reduce(
                  (acc, item) => {
                    acc[item.id] = item.label;
                    return acc;
                  },
                  {} as Record<number, string>,
                );
                onChange?.(selectedObj);
              }}
              options={dropdownOptions?.map((o) => ({ value: String(o.id), label: o.label })) ?? []}
              placeholder={placeholder}
              selectAllLabel="Select all"
              className="border-[#E4E4E7] border-solid w-full h-[30px] min-h-[30px] border-[1px] rounded-[6px] text-[13px] px-2 py-1 text-[#6A6A6A] focus:outline-none focus:ring-2 focus:ring-[#0A8BC7] text-left flex items-center justify-between"
            />
          </div>
        )}
      </div>
      {errorMessage ? <span className="text-red-600 text-sm font-medium">{errorMessage}</span> : ''}
      {isChecked && (
        <>
          <hr className="text-gray-500 -mx-4" />
          <div className="collapsible-section-content border-solid border-[#D4D4D8] pt-2">
            {children}
          </div>
        </>
      )}
    </div>
  );
}

export default CollapsibleSection;
